import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { NgIf } from '@angular/common';
import { ButtonModule } from '@primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { PasswordModule } from '@primeng/password';
import {ToastService} from '@shared/third-part-modules/toastr/toast.service';

@Component({
  selector: 'reset-password',
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    ReactiveFormsModule,
    ButtonModule,
    TranslatePipe,
    InputValidatorDirective,
    PasswordModule
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);
  private passwordResetService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public _toastService = inject(ToastService);

  form: FormGroup;
  isLoading = false;
  success = false;
  error: string | null = null;
  tokenValid = false;
  tokenChecked = false;

  constructor() {
    this.form = this.fb.group({
      token: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      password_confirmation: ['', [Validators.required]]
    }, {
      validators: this.mustMatch('password', 'password_confirmation')
    });
  }

  ngOnInit() {
    const token = this.route.snapshot.params['token'];
    if (token) {
      this.form.patchValue({ token });
      this.tokenValid = true; // Assume token is valid if it exists
      this.tokenChecked = true;
    } else {
      this.tokenChecked = true;
      this.error = 'Invalid or missing reset token';
      this._toastService.success('', "TOAST.TOAST_reset_password_token_invalid");
    }
  }

  onSubmit() {
    if (this.form.invalid || !this.tokenValid) return;

    this.isLoading = true;
    this.error = null;

    const formData = {
      password: this.form.value.password,
      password_confirmation: this.form.value.password_confirmation
    };

    this.passwordResetService.resetPassword(this.form.value.token, formData).subscribe({
      next: () => {
        this.success = true;
        this.isLoading = false;
        setTimeout(() => this.router.navigate(['/auth/login']), 3000);
        this._toastService.success('', "TOAST.TOAST_reset_password_success");
      },
      error: (err) => {
        this.isLoading = false;
        this.tokenValid = false;
        this._toastService.error('', "TOAST.TOAST_reset_password_token_invalid");
        }
    });
  }

  private mustMatch(controlName: string, matchingControlName: string): (formGroup: FormGroup) => void {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }
}
